import React from 'react';

import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BorderedGrid } from '@shared/components/borderedGrid';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { truncateText } from '@shared/utils/truncateText';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CampaignWebhookDraft } from '@features/Admin/Campaign/utils/campaign-webhook-mapper';

interface CampaignWebhookCardProps {
  webhook: CampaignWebhookDraft;
  disabled: boolean;
  busy: boolean;
  onEdit: (webhook: CampaignWebhookDraft) => void;
  onDelete: (webhook: CampaignWebhookDraft) => void;
  onToggle: (webhook: CampaignWebhookDraft, checked: boolean) => void;
}

const CampaignWebhookCard: React.FC<CampaignWebhookCardProps> = ({
  webhook,
  disabled,
  busy,
  onEdit,
  onDelete,
  onToggle,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const getAuthTypeLabel = (authType: CampaignWebhookDraft['authType']) =>
    t(`campaignPage.webhooks.authTypes.${authType}`);

  return (
    <BorderedGrid
      sx={{
        marginBottom: '12px',
        padding: '16px 20px',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '320px',
              }}>
              {webhook.name || t('campaignPage.webhooks.unnamed')}
            </Typography>
            <Chip
              size="small"
              label={webhook.httpMethod}
              sx={{
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: '11px',
                height: '22px',
              }}
            />
            <Chip
              size="small"
              label={
                webhook.isEnabled
                  ? t('campaignPage.webhooks.status.enabled')
                  : t('campaignPage.webhooks.status.disabled')
              }
              sx={{
                backgroundColor: webhook.isEnabled
                  ? theme.palette.success.light
                  : theme.palette.grey[200],
                color: webhook.isEnabled ? theme.palette.success.dark : theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '11px',
                height: '22px',
              }}
            />
            <Chip
              size="small"
              label={getAuthTypeLabel(webhook.authType)}
              sx={{
                backgroundColor: theme.palette.grey[200],
                color: theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '11px',
                height: '22px',
              }}
            />
            {webhook.hasStoredAuth ? (
              <Chip
                size="small"
                label={t('campaignPage.webhooks.authConfigured')}
                sx={{
                  backgroundColor: theme.palette.warning.light,
                  color: theme.palette.warning.dark,
                  fontWeight: 500,
                  fontSize: '11px',
                  height: '22px',
                }}
              />
            ) : null}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Events: {webhook.events === null ? 'Legacy call ended' : webhook.events.join(', ')}
          </Typography>
          {webhook.lastEventFailure && (
            <Alert severity="error">
              A {webhook.lastEventFailure.event} delivery snapshot is unavailable. No request was
              dispatched for that receipt; operator review is required.
            </Alert>
          )}
          <Tooltip title={webhook.url} placement="bottom-start">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '560px',
                mb: 1,
              }}>
              {truncateText(webhook.url, 52)}
            </Typography>
          </Tooltip>

          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            {webhook.headers.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('campaignPage.webhooks.headersCount', { count: webhook.headers.length })}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {webhook.headers.length > 0 ? '• ' : ''}
              {webhook.bodyTemplate.trim()
                ? t('campaignPage.webhooks.customPayload')
                : t('campaignPage.webhooks.defaultPayload')}
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <FormGroup sx={{ mr: 0.5 }}>
            <FormControlLabel
              sx={{ marginRight: 0, marginLeft: 0 }}
              control={
                <CustomSwitch
                  checked={webhook.isEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onToggle(webhook, event.target.checked)
                  }
                  disabled={disabled || busy}
                />
              }
              label=""
            />
          </FormGroup>

          <Tooltip title={t('common.edit')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onEdit(webhook)}
                disabled={disabled || busy}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                }}>
                <IconEdit size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t('common.delete')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(webhook)}
                disabled={disabled || busy}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light,
                  },
                }}>
                <IconTrash size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </BorderedGrid>
  );
};

export default CampaignWebhookCard;
